<template>
  <ion-card ref="card" class="ion-margin" v-if="Object.keys(chartData).length">
    <ion-card-header>
      <ion-card-subtitle>
        {{ subTitle }}
      </ion-card-subtitle>
    </ion-card-header>
    <ion-card-content>
      <div class="card-main">
        {{ getValueDescription() }}
      </div>
      <Bar
        :options="chartOptions"
        :data="chartData"
      />
      <div class="card-footer ion-text-center">{{ additionalInfo }}</div>
    </ion-card-content>
  </ion-card>
</template>

<script setup lang="ts">
import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle } from "@ionic/vue";
import { defineProps, onMounted, ref } from "vue";
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { useMotion } from "@vueuse/motion";

ChartJS.register(ArcElement, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

const props = defineProps({
  subTitle: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: false
  },
  data: {
    type: Object,
    required: true
  }
})

let chartData = ref({}) as any
let chartOptions = ref({})
let additionalInfo = ref("")
let primaryColor = ref("")
let textColor = ref("white")
const card = ref()

let style = window.getComputedStyle(document.body)

const { variant } = useMotion(card, {
  initial: {
    opacity: 0,
    y: 100
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 300,
      type: 'keyframes',
      ease: 'easeIn',
    },
  }
})

onMounted(() => {
  primaryColor.value = style.getPropertyValue("--ion-color-primary")
  textColor.value = style.getPropertyValue("--ion-text-color")

  chartData.value = {
    labels: props.data?.map(() => ""),
    datasets: [{
      label: "",
      data: props.data?.map((value: any) => value.TableItemMeta.includes("-") ? +value.TableItemMeta.split("-")[0] : +value.TableItemMeta),
      backgroundColor: ["transparent", ...props.data?.map(() => "#676767")],
      borderWidth: "1",
      borderRadius: "5",
      borderColor: [primaryColor.value, ...props.data?.map(() => "transparent")]
    }]
  }

  chartOptions.value = {
    responsive: true,
    events: [],
    plugins: {
      legend: {
        display: false
      }  // hide the legend data
    },
    scales: {
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        border: {
          display: false
        }
      },
      x: {
        grid: {
          display: false,
        },
        border: {
          display: true,
          color: textColor.value
        }
      }
    },
  }

  additionalInfo.value = props.data?.[0]?.TableItemLabel
})

function getValueDescription() {
  const val = props.data[0].TableItemMeta
  let seconds = ""
  let time = ""

  if(val.includes("-")) {
    [seconds, time] = val.split("-")
  }

  if(time.includes(",")) {
    return time.split(",")[0]
  } else {
    const data = time.split(":") as any
    return data[0] == 0 ? data[1] == 0 ? data[2] + " sec." : data[1] + " min." : data[0] + "hours"
  }
}
</script>

<style scoped>

.bar-chart {
  margin-inline-start: auto;
  margin-block-start: -20px;
}

.card-main {
  position: absolute;
}

</style>